import { ChangeEvent, useRef, useState } from 'react';
import { Download, Eye, FileText, RotateCcw, Sparkles, Trash2, Upload } from 'lucide-react';
import { toast } from 'react-toastify';
import { DateField } from '../DateField/DateField';
import { PdfPreviewDialog } from '../PdfPreviewDialog/PdfPreviewDialog';
import {
  useCarrierDocuments,
  useDeleteCarrierDocument,
  useDownloadCarrierDocument,
  useExtractCarrierDocument,
  usePreviewCarrierDocument,
  useRestoreCarrierDocument,
  useUploadCarrierDocument,
} from '../../features/carrier-documents/api';
import { CarrierDocument, CarrierDocumentType } from '../../features/carriers/types';
import './CarrierDocumentsPanel.scss';

const DOC_TYPE_OPTIONS: { value: CarrierDocumentType; label: string }[] = [
  { value: 'COI', label: 'Certificate of Insurance' },
  { value: 'W9', label: 'W-9' },
  { value: 'AGREEMENT', label: 'Agreement' },
  { value: 'AUTHORITY', label: 'Authority (MC/DOT)' },
  { value: 'OTHER', label: 'Other' },
];

const DOC_TYPE_LABEL = DOC_TYPE_OPTIONS.reduce(
  (acc, o) => ({ ...acc, [o.value]: o.label }),
  {} as Record<CarrierDocumentType, string>
);

const MAX_FILE_SIZE_BYTES = 15 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.csv',
  '.txt',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
];

function getExtension(fileName: string) {
  const i = fileName.lastIndexOf('.');
  return i < 0 ? '' : fileName.slice(i).toLowerCase();
}

function validateFile(file: File): string | null {
  if (file.size > MAX_FILE_SIZE_BYTES) return 'The document must be 15 MB or smaller.';
  if (!ACCEPTED_EXTENSIONS.includes(getExtension(file.name)))
    return 'This document type is not supported.';
  return null;
}

function formatFileSize(size?: number | null) {
  if (!size) return '—';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

// UTC-safe day formatting, matching the app's ISO-slicing convention.
function formatDay(iso?: string | null) {
  if (!iso) return null;
  const [y, m, d] = iso.slice(0, 10).split('-');
  return m && d && y ? `${m}/${d}/${y}` : null;
}

function isExpired(iso?: string | null) {
  if (!iso) return false;
  return iso.slice(0, 10) < new Date().toISOString().slice(0, 10);
}

function isPdf(doc: CarrierDocument) {
  return (doc.mimeType || '').includes('pdf') || doc.fileName.toLowerCase().endsWith('.pdf');
}

function mapUploadError(err: any): string {
  const code = err?.response?.data?.error;
  if (code === 'FILE_TOO_LARGE') return 'The document must be 15 MB or smaller.';
  if (code === 'UNSUPPORTED_FILE_TYPE') return 'This document type is not supported.';
  if (code === 'DOCUMENT_LIMIT_REACHED') return 'This carrier has reached the document limit.';
  return 'Could not upload the document.';
}

export function CarrierDocumentsPanel({ carrierId }: { carrierId: string }) {
  const [showArchived, setShowArchived] = useState(false);
  const { data, isLoading } = useCarrierDocuments(carrierId, showArchived);
  const upload = useUploadCarrierDocument(carrierId);
  const remove = useDeleteCarrierDocument(carrierId);
  const restore = useRestoreCarrierDocument(carrierId);
  const download = useDownloadCarrierDocument(carrierId);
  const preview = usePreviewCarrierDocument(carrierId);
  const extract = useExtractCarrierDocument(carrierId);
  const [extractingId, setExtractingId] = useState<string | null>(null);

  const [type, setType] = useState<CarrierDocumentType>('COI');
  const [file, setFile] = useState<File | null>(null);
  const [expiresAt, setExpiresAt] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const documents: CarrierDocument[] = data?.documents ?? [];

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    const err = validateFile(picked);
    if (err) {
      toast.error(err);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setFile(picked);
  };

  const onUpload = async () => {
    if (!file) return;
    try {
      const res = await upload.mutateAsync({ file, type, expiresAt: expiresAt || undefined });
      setFile(null);
      setExpiresAt('');
      if (inputRef.current) inputRef.current.value = '';

      // A COI is read automatically on upload; other types use the manual button.
      const newDoc = (res as any)?.document as CarrierDocument | undefined;
      if (type === 'COI' && newDoc?.id) {
        toast.success('COI uploaded — reading with AI…');
        await onExtract(newDoc);
      } else {
        toast.success('Document uploaded');
      }
    } catch (err) {
      toast.error(mapUploadError(err));
    }
  };

  const onPreview = async (doc: CarrierDocument) => {
    try {
      const url = await preview.mutateAsync(doc.id);
      setPreviewTitle(doc.fileName);
      setPreviewUrl(url);
    } catch {
      toast.error('Could not open preview.');
    }
  };

  const onDelete = async (doc: CarrierDocument) => {
    // COIs are archived (kept for audits), everything else is truly deleted.
    const isCoi = doc.type === 'COI';
    const message = isCoi
      ? `Archive "${doc.fileName}"? It stays retained for audits and can be restored.`
      : `Delete "${doc.fileName}"? This cannot be undone.`;
    if (!window.confirm(message)) return;
    try {
      await remove.mutateAsync(doc.id);
      toast.success(isCoi ? 'COI archived' : 'Document deleted');
    } catch {
      toast.error(isCoi ? 'Could not archive the document.' : 'Could not delete the document.');
    }
  };

  const onRestore = async (doc: CarrierDocument) => {
    try {
      await restore.mutateAsync(doc.id);
      toast.success('COI restored');
    } catch {
      toast.error('Could not restore the document.');
    }
  };

  const onExtract = async (doc: CarrierDocument) => {
    setExtractingId(doc.id);
    try {
      const result = await extract.mutateAsync(doc.id);
      const exp = result.document?.expiresAt;
      const filled = result.applied?.billingFields?.length ?? 0;
      const parts: string[] = [];
      if (exp) parts.push(`expires ${exp.slice(0, 10)}`);
      if (filled) parts.push(`filled ${filled} billing field${filled === 1 ? '' : 's'}`);
      toast.success(parts.length ? `Read document — ${parts.join(', ')}.` : 'Document read.');
    } catch (err: any) {
      const code = err?.response?.data?.error;
      if (code === 'AI_READER_NOT_CONFIGURED') {
        toast.error('The AI reader is not configured on the server.');
      } else if (code === 'UNSUPPORTED_DOCUMENT_TYPE') {
        toast.error('This file type cannot be read.');
      } else {
        toast.error('Could not read the document.');
      }
    } finally {
      setExtractingId(null);
    }
  };

  return (
    <div className="carrier-docs">
      <div className="carrier-docs__header">
        <div className="carrier-docs__title">
          <FileText size={14} />
          <span>Documents</span>
        </div>
      </div>

      <div className="carrier-docs__form">
        <div className="carrier-docs__form-grid">
          <div className="form-control">
            <label>Type</label>
            <select value={type} onChange={(e) => setType(e.target.value as CarrierDocumentType)}>
              {DOC_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-control">
            <label>Expires (optional)</label>
            <DateField
              value={expiresAt}
              onChange={setExpiresAt}
              placeholder="mm/dd/yyyy"
              clearable
            />
          </div>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS.join(',')}
          onChange={onPick}
          style={{ display: 'none' }}
        />
        <div className="carrier-docs__form-actions">
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => inputRef.current?.click()}
          >
            {file ? file.name : 'Choose file'}
          </button>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={onUpload}
            disabled={!file || upload.isPending}
          >
            <Upload size={14} />
            {upload.isPending ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>

      <div className="carrier-docs__toolbar">
        <label className="carrier-docs__archived-toggle">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          Show archived COIs
        </label>
      </div>

      {isLoading ? (
        <div className="skeleton" style={{ height: 60 }} />
      ) : documents.length === 0 ? (
        <p className="carrier-docs__empty muted">No documents yet.</p>
      ) : (
        <div className="carrier-docs__list">
          {documents.map((doc) => {
            const expiry = formatDay(doc.expiresAt);
            const expired = isExpired(doc.expiresAt);
            const archived = !!doc.deletedAt;
            return (
              <div
                key={doc.id}
                className={`carrier-docs__card${archived ? ' carrier-docs__card--archived' : ''}`}
              >
                <div className="carrier-docs__card-top">
                  <div className="carrier-docs__card-info">
                    <div className="carrier-docs__card-name">
                      <span className="carrier-docs__badge">
                        {DOC_TYPE_LABEL[doc.type] ?? doc.type}
                      </span>
                      {archived && (
                        <span className="carrier-docs__badge carrier-docs__badge--archived">
                          Archived
                        </span>
                      )}
                      <span title={doc.fileName}>{doc.fileName}</span>
                    </div>
                    <div className="carrier-docs__card-detail">
                      {formatFileSize(doc.sizeBytes)}
                      {expiry && (
                        <>
                          {' · '}
                          <span className={expired ? 'carrier-docs__expired' : undefined}>
                            {expired ? 'Expired' : 'Expires'} {expiry}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="carrier-docs__card-actions">
                    <button
                      type="button"
                      className="btn btn--icon"
                      title="Read with AI — extract details and update the profile"
                      onClick={() => onExtract(doc)}
                      disabled={extractingId === doc.id}
                    >
                      <Sparkles
                        size={16}
                        className={extractingId === doc.id ? 'spin' : undefined}
                      />
                    </button>
                    {isPdf(doc) && (
                      <button
                        type="button"
                        className="btn btn--icon"
                        title="Preview"
                        onClick={() => onPreview(doc)}
                      >
                        <Eye size={16} />
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn--icon"
                      title="Download"
                      onClick={() =>
                        download.mutate({ documentId: doc.id, fileName: doc.fileName })
                      }
                    >
                      <Download size={16} />
                    </button>
                    {archived ? (
                      <button
                        type="button"
                        className="btn btn--icon"
                        title="Restore"
                        onClick={() => onRestore(doc)}
                        disabled={restore.isPending}
                      >
                        <RotateCcw size={16} />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn--icon btn--danger"
                        title={doc.type === 'COI' ? 'Archive' : 'Delete'}
                        onClick={() => onDelete(doc)}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>

                {(doc.extractedData?.coverages?.length ?? 0) > 0 && (
                  <div className="carrier-docs__coverages">
                    <table className="coi-table">
                      <thead>
                        <tr>
                          <th>Insurer</th>
                          <th>Type</th>
                          <th>Policy #</th>
                          <th>Start</th>
                          <th>End</th>
                        </tr>
                      </thead>
                      <tbody>
                        {doc.extractedData!.coverages!.map((cov, i) => (
                          <tr
                            key={i}
                            className={
                              isExpired(cov.expirationDate) ? 'coi-table__row--expired' : undefined
                            }
                          >
                            <td>{cov.insurerName || '—'}</td>
                            <td>{cov.type || '—'}</td>
                            <td>{cov.policyNumber || '—'}</td>
                            <td>{formatDay(cov.effectiveDate) || '—'}</td>
                            <td>{formatDay(cov.expirationDate) || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {previewUrl && (
        <PdfPreviewDialog
          url={previewUrl}
          title={previewTitle}
          onClose={() => setPreviewUrl(null)}
        />
      )}
    </div>
  );
}
